/**
 * DEC-329 — the company's dispatcher-completion policy, and the approval requests it creates,
 * as the stuck-jobs page needs them (quick 261013-dca).
 *
 * `forceReviewMode` is the one place the page decides what a viewer may do with a stuck job
 * that has a captured load. It mirrors routes/jobs/force-review.ts in the API, which enforces
 * the same table; this only words the button:
 *
 *   NEVER                → 'NONE'    nobody sees a button
 *   ALWAYS               → 'DIRECT'  for COMPLETION_REQUEST_ROLES
 *   SUPERVISOR_REQUIRED  → 'DIRECT'  for COMPLETION_APPROVER_ROLES (self-approved)
 *                          'REQUEST' for the rest of COMPLETION_REQUEST_ROLES
 *   any role outside COMPLETION_REQUEST_ROLES → 'NONE'
 *
 * The policy list is local, not imported from @lodex-pro/shared: the shared barrel pulls
 * drizzle into a web bundle.
 */

import { useApiQuery, type useApiOpts } from '@lodex-pro/web-shared';
import { COMPLETION_APPROVER_ROLES, COMPLETION_REQUEST_ROLES } from '../../lib/role-gates';

export type DispatcherCompletionPolicy = 'NEVER' | 'SUPERVISOR_REQUIRED' | 'ALWAYS';
export type ForceReviewMode = 'NONE' | 'DIRECT' | 'REQUEST';

export function forceReviewMode(
  policy: DispatcherCompletionPolicy | null,
  role: string,
): ForceReviewMode {
  if (policy === null || policy === 'NEVER') return 'NONE';
  if (!COMPLETION_REQUEST_ROLES.has(role)) return 'NONE';
  if (policy === 'ALWAYS') return 'DIRECT';
  return COMPLETION_APPROVER_ROLES.has(role) ? 'DIRECT' : 'REQUEST';
}

export function canApproveCompletionRole(role: string): boolean {
  return COMPLETION_APPROVER_ROLES.has(role);
}

export const COMPLETION_POLICY_QUERY_KEY = ['tenants', 'completion-policy'] as const;

export function useCompletionPolicy(
  apiOpts: ReturnType<typeof useApiOpts>,
): ReturnType<typeof useApiQuery<{ readonly policy: DispatcherCompletionPolicy }>> {
  return useApiQuery<{ readonly policy: DispatcherCompletionPolicy }>(
    apiOpts,
    '/v1/tenants/completion-policy',
    { queryKey: COMPLETION_POLICY_QUERY_KEY },
  );
}

export interface CompletionRequestRow {
  readonly id: string;
  readonly jobId: string;
  readonly haulId: string | null;
  readonly status: 'PENDING' | 'APPROVED' | 'DECLINED' | 'CANCELLED';
  readonly reason: string;
  readonly requestedAt: string;
  readonly requestedById: string | null;
  readonly requestedByName: string | null;
  readonly driverName: string | null;
  readonly siteName: string | null;
}

/** Under ['jobs'] so every stuck-page mutation's invalidation refreshes it too. */
export const PENDING_COMPLETION_REQUESTS_QUERY_KEY = [
  'jobs',
  'completion-requests',
  'PENDING',
] as const;

export function usePendingCompletionRequests(
  apiOpts: ReturnType<typeof useApiOpts>,
  enabled: boolean,
): ReturnType<typeof useApiQuery<{ readonly requests: readonly CompletionRequestRow[] }>> {
  return useApiQuery<{ readonly requests: readonly CompletionRequestRow[] }>(
    apiOpts,
    '/v1/dispatcher-completion-requests?status=PENDING',
    { queryKey: PENDING_COMPLETION_REQUESTS_QUERY_KEY, refetchInterval: 60_000, enabled },
  );
}
